import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { api, ApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type { MessageKey } from "../lib/messages";
import { useSession } from "../lib/session";
import { useAsync } from "../lib/useAsync";
import { IconCalendar, IconCall, IconCheck, IconChecklist, IconDeal, IconFlag, IconPin, IconReceipt, IconSend, IconShield, IconStar, IconTag, IconWhatsApp, IconX } from "../ui/icons";
import { AsyncButton, ConfirmDialog, Empty, ErrorState, Loading, MoneyInput, Notice, PageHeader, QtyStepper, TrustBadges, TrustLine, useToast, type Trust } from "../ui/kit";
import { SafetySheet } from "./Listing";

// ---------------------------------------------------------------------------
// Shapes from /api/conversations
// ---------------------------------------------------------------------------
type DealStatus = "open" | "agreed" | "reserved" | "completed" | "cancelled";
type Needs = "answer_price" | "confirm_received" | "confirm_handover" | "reply" | null;
type MsgKind = "text" | "proposal" | "contact" | "event";
type Preview = { kind: MsgKind; body: string; meta: Record<string, any>; mine: boolean };
type Conv = {
  id: string;
  listingId: string;
  listingTitle: string;
  priceCents: number;
  currency: string;
  coverUrl: string | null;
  role: "buyer" | "seller";
  otherName: string;
  deal: { status: DealStatus; quantity: number; agreedPriceCents: number | null; source: string };
  lastMessage: Preview | null;
  lastMessageAt: string;
  unread: number;
  needs: Needs;
};
type Msg = { id: string; mine: boolean; kind: MsgKind; body: string; meta: Record<string, any>; masked: boolean; createdAt: string };
type Deal = {
  status: DealStatus;
  source: string;
  quantity: number;
  agreedPriceCents: number | null;
  totalCents: number;
  meetup: { place: string | null; at: string | null } | null;
  agreedAt: string | null;
  reservedAt: string | null;
  sellerConfirmedAt: string | null;
  buyerConfirmedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelledByMe: boolean | null;
  cancelReason: string | null;
  checklist: { key: string; done: boolean }[];
  receiptCode: string | null;
  reviewed: boolean;
  actions: { propose: boolean; reserve: boolean; meetup: boolean; confirm: boolean; cancel: boolean; checklist: boolean; review: boolean };
  /** A help request about this deal, still open; or whether one can be opened (during the deal, and a week after). */
  openIssue: { id: string; refNo: number } | null;
  canReport: boolean;
};
type ThreadResp = {
  conversation: {
    id: string;
    listingId: string;
    listingTitle: string;
    priceCents: number;
    currency: string;
    condition: string;
    city: string;
    negotiable: boolean;
    quantityAvailable: number;
    listingStatus: string;
    coverUrl: string | null;
    role: "buyer" | "seller";
    otherId: string;
    otherName: string;
    otherTrust?: Trust;
  };
  deal: Deal;
  messages: Msg[];
};

const STATUS_TONE: Record<DealStatus, string> = { open: "", agreed: "offer", reserved: "offer", completed: "good", cancelled: "bad" };

/** One line describing any message, for the inbox preview. */
function usePreview() {
  const { t, money } = useI18n();
  return (m: Preview | null, currency: string) => {
    if (!m) return "";
    if (m.kind === "text") return m.body;
    if (m.kind === "proposal") return t(m.mine ? "msg.youProposed" : "msg.theyProposed", { p: money(m.meta.priceCents * (m.meta.quantity ?? 1), currency) });
    if (m.kind === "contact") return t(m.meta.method === "whatsapp" ? "msg.sharedWhatsapp" : "msg.sharedNumber");
    return t(`event.${m.body}` as MessageKey, { p: m.meta.priceCents ? money(m.meta.priceCents, currency) : "", place: m.meta.place ?? "" });
  };
}

// ---------------------------------------------------------------------------
// The inbox: every deal, buying and selling, with what needs you first
// ---------------------------------------------------------------------------
export function Inbox() {
  const { t, relative } = useI18n();
  const { me, loading: sLoading, refresh } = useSession();
  const [params, setParams] = useSearchParams();
  const role = params.get("role") ?? "all";
  const state = params.get("state") ?? "active";
  const preview = usePreview();
  const { data, error, loading, reload } = useAsync(
    () => (me ? api<{ conversations: Conv[] }>(`/api/conversations?role=${role}&state=${state}`) : Promise.resolve({ conversations: [] })),
    [me?.id, role, state],
  );
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!sLoading && !me) return <Navigate to="/signin?next=/inbox" replace />;
  const set = (k: string, v: string) => {
    const n = new URLSearchParams(params);
    n.set(k, v);
    setParams(n, { replace: true });
  };
  const convs = data?.conversations ?? [];
  // What needs this person floats to the top; the rest keep their order (most recent first).
  const sorted = [...convs.filter((c) => c.needs), ...convs.filter((c) => !c.needs)];
  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <PageHeader title={t("deals.title")} />
      <div className="row between wrap" style={{ marginBottom: "var(--s4)", gap: "var(--s2)" }}>
        <div className="seg" role="tablist" aria-label={t("deals.title")}>
          {(["all", "buying", "selling"] as const).map((r) => (
            <button key={r} role="tab" aria-selected={role === r} onClick={() => set("role", r)}>{t(`deals.role.${r}`)}</button>
          ))}
        </div>
        <div className="seg" role="tablist">
          {(["active", "done"] as const).map((s) => (
            <button key={s} role="tab" aria-selected={state === s} onClick={() => set("state", s)}>{t(`deals.state.${s}`)}</button>
          ))}
        </div>
      </div>
      {loading && !data ? (
        <Loading />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : !sorted.length ? (
        <Empty
          title={t(state === "done" ? "deals.emptyDone" : "deals.empty")}
          body={t("deals.emptyBody")}
          action={<Link className="btn primary" to="/">{t("welcome.browse")}</Link>}
        />
      ) : (
        <div className="list">
          {sorted.map((c) => (
            <Link key={c.id} className={`list-item deal-item${c.needs ? " needs" : ""}`} to={`/inbox/${c.id}`}>
              {c.coverUrl ? <img src={c.coverUrl} alt="" /> : <div className="ph" />}
              <div className="grow">
                <div className="row between">
                  <span className={`t ${c.unread ? "unread" : ""}`} dir="auto">{c.otherName}</span>
                  <span className="tiny muted">{relative(c.lastMessageAt)}</span>
                </div>
                <div className="s" dir="auto">{c.listingTitle}</div>
                <div className="row wrap" style={{ gap: 6, marginTop: 4 }}>
                  <span className={`tag ${STATUS_TONE[c.deal.status]}`}>{t(`deal.status.${c.deal.status}`)}</span>
                  <span className="tag">{t(c.role === "buyer" ? "inbox.buying" : "inbox.selling")}</span>
                  {c.deal.source !== "chat" && (
                    <span className="tag">{c.deal.source === "whatsapp" ? <IconWhatsApp size={13} /> : <IconCall size={13} />} {t(`deal.source.${c.deal.source}` as MessageKey)}</span>
                  )}
                  {c.needs && <span className="tag needs">{t(`needs.${c.needs}`)}</span>}
                </div>
                <div className={`s ${c.unread ? "unread" : ""}`} dir="auto" style={{ marginTop: 4 }}>{preview(c.lastMessage, c.currency)}</div>
              </div>
              {c.unread > 0 && <span className="badge-dot" style={{ position: "static" }}>{c.unread}</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The deal room
// ---------------------------------------------------------------------------
const STEPS = ["contact", "price", "reserved", "handover", "done"] as const;

function stepIndex(d: Deal) {
  if (d.status === "completed") return 4;
  if (d.sellerConfirmedAt || d.buyerConfirmedAt) return 3;
  if (d.status === "reserved") return 3;
  if (d.status === "agreed") return 2;
  return 1;
}

export function Thread() {
  const { id } = useParams();
  const { t, money, date } = useI18n();
  const toast = useToast();
  const { refresh } = useSession();
  const { data, error, loading, reload, setData } = useAsync(() => api<ThreadResp>(`/api/conversations/${id}/messages`), [id]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sheet, setSheet] = useState<null | "propose" | "meetup" | "checklist" | "cancel" | "confirm" | "share">(null);
  const endRef = useRef<HTMLDivElement>(null);
  const safety = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [data?.messages.length]);
  // Poll while the deal is open, so the other side's moves appear without a refresh.
  useEffect(() => {
    const h = setInterval(() => document.visibilityState === "visible" && reload(), 8000);
    return () => clearInterval(h);
  }, [reload]);
  useEffect(() => {
    if (data) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.messages.length]);

  if (loading && !data) return <Loading />;
  if (error || !data) return <ErrorState error={error} onRetry={reload} />;
  const c = data.conversation;
  const d = data.deal;
  const seller = c.role === "seller";
  const anyMasked = data.messages.some((m) => m.masked);
  const step = stepIndex(d);

  const act = async (path: string, body: unknown = {}, done?: string, method = "POST") => {
    setErr(null);
    try {
      await api(`/api/conversations/${id}/${path}`, { method, body });
      if (done) toast(done);
      await reload();
    } catch (e) {
      setErr((e as ApiError).message);
      throw e;
    }
  };

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    setErr(null);
    try {
      const m = await api<Msg>(`/api/conversations/${id}/messages`, { body: { body } });
      setData({ ...data, messages: [...data.messages, { ...m, meta: {}, mine: true }] });
      setText("");
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setSending(false);
    }
  };

  const openProposal = data.messages.find((m) => m.kind === "proposal" && m.meta.status === "open");

  return (
    <div className="deal-room" style={{ maxWidth: 760, margin: "0 auto" }}>
      <PageHeader title={c.otherName} back="/inbox" />

      <Link to={`/l/${c.listingId}`} className="card row deal-listing">
        {c.coverUrl ? <img src={c.coverUrl} alt="" /> : <span className="ph" />}
        <div className="grow">
          <div style={{ fontWeight: 600 }} dir="auto">{c.listingTitle}</div>
          <div className="small muted">
            {t(seller ? "inbox.selling" : "inbox.buying")} · <span dir="auto">{c.city}</span>
            {c.listingStatus !== "active" && ` · ${t(`sell.status.${c.listingStatus}` as MessageKey)}`}
          </div>
        </div>
        <span className="price num" style={{ fontSize: 18 }}>{money(c.priceCents, c.currency)}</span>
      </Link>

      {c.otherTrust && (
        <div className="other-trust small">
          <span className="muted">{t(seller ? "deal.aboutBuyer" : "deal.aboutSeller")}</span>
          {seller ? (
            <span>{c.otherTrust.purchases ? t("trust.purchases", { n: c.otherTrust.purchases }) : t("trust.new")}{c.otherTrust.rating.count ? ` · ${c.otherTrust.rating.average} ★` : ""}</span>
          ) : (
            <TrustLine trust={c.otherTrust} />
          )}
          <TrustBadges trust={c.otherTrust} compact />
          <Link className="link" to={`/s/${c.otherId}`}>{t("deal.profile")}</Link>
        </div>
      )}

      {d.status !== "cancelled" && (
        <ol className="deal-steps" aria-label={t("deal.progress")}>
          {STEPS.map((s, i) => (
            <li key={s} className={i < step ? "done" : i === step ? "now" : ""} aria-current={i === step ? "step" : undefined}>
              <span className="dot">{i < step ? <IconCheck size={12} /> : i + 1}</span>
              <span className="lbl">{t(`deal.step.${s}`)}</span>
            </li>
          ))}
        </ol>
      )}

      <NextStep thread={data} onOpen={setSheet} onSafety={() => safety.current?.showModal()} act={act} reload={reload} />
      {d.openIssue ? (
        <Link className="small row" style={{ gap: 6, marginBottom: "var(--s3)" }} to={`/support/${d.openIssue.id}`}><IconFlag size={14} /> {t("deal.openIssue", { n: d.openIssue.refNo })}</Link>
      ) : d.canReport ? (
        <Link className="small muted row" style={{ gap: 6, marginBottom: "var(--s3)" }} to={`/inbox/${c.id}/problem`}><IconFlag size={14} /> {t("deal.reportProblem")}</Link>
      ) : null}

      <div className="thread" aria-live="polite">
        {data.messages.map((m) => (
          <MessageView key={m.id} m={m} currency={c.currency} seller={seller} otherName={c.otherName} canAnswer={d.actions.propose} onRespond={(action) => act(`proposals/${m.id}`, { action })} />
        ))}
        {anyMasked && <Notice>{t("inbox.masked")}</Notice>}
        {err && <Notice kind="bad">{err}</Notice>}
        <div ref={endRef} />
      </div>

      {d.status !== "cancelled" && d.status !== "completed" && (
        <div className="deal-tools" role="toolbar" aria-label={t("deal.tools")}>
          {d.actions.propose && (
            <button className="chip" onClick={() => setSheet("propose")}><IconTag size={16} /> {t(openProposal ? "deal.counter" : "deal.propose")}</button>
          )}
          {d.actions.meetup && <button className="chip" onClick={() => setSheet("meetup")}><IconPin size={16} /> {t("deal.meetup")}</button>}
          {d.actions.checklist && <button className="chip" onClick={() => setSheet("checklist")}><IconChecklist size={16} /> {t("deal.checklist")}</button>}
          <button className="chip" onClick={() => setSheet("share")}><IconWhatsApp size={16} /> {t("deal.shareNumber")}</button>
          {d.actions.cancel && <button className="chip" onClick={() => setSheet("cancel")}><IconX size={16} /> {t("deal.cancel")}</button>}
        </div>
      )}

      <form className="composer" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <div className="inner">
          <label htmlFor="msg" className="sr-only">{t("inbox.placeholder")}</label>
          <input id="msg" className="input grow" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("inbox.placeholder")} maxLength={2000} autoComplete="off" dir="auto" />
          <button className="btn primary" disabled={sending || !text.trim()} aria-label={t("inbox.send")}><IconSend size={18} /></button>
        </div>
      </form>

      {sheet === "propose" && <ProposeSheet thread={data} onClose={() => setSheet(null)} onSend={(priceCents, quantity) => act("proposals", { priceCents, quantity }, t("deal.proposalSent"))} />}
      {sheet === "meetup" && <MeetupSheet deal={d} onClose={() => setSheet(null)} onSave={(place, at) => act("meetup", { place, at }, t("deal.meetupSent"))} />}
      {sheet === "checklist" && <ChecklistSheet deal={d} onClose={() => setSheet(null)} onToggle={(item, done) => act("checklist", { item, done }, undefined, "PUT")} />}
      {sheet === "share" && <ShareSheet onClose={() => setSheet(null)} onShare={(method) => act("share-contact", { method }, t("deal.numberShared"))} />}
      <ConfirmDialog
        open={sheet === "cancel"}
        title={t("deal.cancelTitle")}
        confirmLabel={t("deal.cancel")}
        onClose={() => setSheet(null)}
        onConfirm={() => act("cancel", { reason: (document.getElementById("cancel-reason") as HTMLTextAreaElement | null)?.value ?? "" }, t("deal.cancelled"))}
        body={
          <div className="stack">
            <p className="muted" style={{ margin: 0 }}>{t(d.reservedAt ? "deal.cancelReserved" : "deal.cancelBody")}</p>
            <label className="label" htmlFor="cancel-reason">{t("deal.cancelReason")}</label>
            <textarea id="cancel-reason" className="input" maxLength={300} dir="auto" />
          </div>
        }
      />
      <ConfirmDialog
        open={sheet === "confirm"}
        title={t(seller ? "deal.confirmHandoverTitle" : "deal.confirmReceivedTitle")}
        confirmLabel={t(seller ? "deal.confirmHandover" : "deal.confirmReceived")}
        onClose={() => setSheet(null)}
        onConfirm={() => act("confirm", { confirm: true }, t("deal.confirmed"))}
        body={<p className="muted" style={{ margin: 0 }}>{t(seller ? "deal.confirmHandoverBody" : "deal.confirmReceivedBody", { total: money(d.totalCents, c.currency) })}</p>}
      />
      <SafetySheet sheet={safety} />
      {d.completedAt && <p className="tiny muted center">{t("deal.completedOn", { d: date(d.completedAt) })}</p>}
    </div>
  );
}

/** The one thing to do next, said plainly, with its button. */
function NextStep({ thread, onOpen, onSafety, act, reload }: {
  thread: ThreadResp;
  onOpen: (s: "propose" | "meetup" | "checklist" | "confirm") => void;
  onSafety: () => void;
  act: (path: string, body?: unknown, done?: string) => Promise<void>;
  reload: () => Promise<void>;
}) {
  const { t, money, date } = useI18n();
  const c = thread.conversation;
  const d = thread.deal;
  const seller = c.role === "seller";
  const price = money(d.totalCents, c.currency);
  const openProposal = thread.messages.find((m) => m.kind === "proposal" && m.meta.status === "open");
  const meet = d.meetup?.place ? (
    <div className="meet-line small"><IconPin size={16} /> <span dir="auto">{d.meetup.place}</span>{d.meetup.at && <> · <IconCalendar size={16} /> {date(d.meetup.at, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</>}</div>
  ) : null;

  let title: string;
  let body: string | null = null;
  let actions: ReactNode = null;
  if (d.status === "cancelled") {
    title = t(d.cancelledByMe ? "next.cancelledByYou" : "next.cancelledByThem");
    body = d.cancelReason;
  } else if (d.status === "completed") {
    title = t("next.completed");
    body = t("next.completedBody", { total: price });
    actions = (
      <>
        <Link className="btn primary" to={`/inbox/${c.id}/receipt`}><IconReceipt size={18} /> {t("deal.receipt")}</Link>
        {d.actions.review && <ReviewForm dealId={c.id} otherName={c.otherName} onDone={reload} />}
        {d.reviewed && <span className="tag good"><IconStar size={14} /> {t("deal.reviewedDone")}</span>}
      </>
    );
  } else if (seller && d.buyerConfirmedAt && !d.sellerConfirmedAt) {
    title = t("next.sellerBuyerConfirmed");
    body = t("next.sellerBuyerConfirmedBody");
    actions = <button className="btn primary" onClick={() => onOpen("confirm")}><IconDeal size={18} /> {t("deal.confirmHandover")}</button>;
  } else if (!seller && d.sellerConfirmedAt && !d.buyerConfirmedAt) {
    title = t("next.buyerSellerConfirmed");
    body = t("next.buyerSellerConfirmedBody", { total: price });
    actions = <button className="btn primary" onClick={() => onOpen("confirm")}><IconDeal size={18} /> {t("deal.confirmReceived")}</button>;
  } else if (seller && d.sellerConfirmedAt) {
    title = t("next.waitingBuyer");
  } else if (!seller && d.buyerConfirmedAt) {
    title = t("next.waitingSeller");
  } else if (openProposal && (d.status === "open" || d.status === "agreed")) {
    // A price is on the table: answering it is the next step for one side, waiting for the other.
    const total = money(openProposal.meta.priceCents * (openProposal.meta.quantity ?? 1), c.currency);
    if (openProposal.mine) {
      title = t("next.waitingPrice", { name: c.otherName, p: total });
      body = t("next.waitingPriceBody");
    } else {
      title = t("next.answerPrice", { name: c.otherName, p: total });
      body = t("next.answerPriceBody", { asking: money(c.priceCents, c.currency) });
      actions = (
        <>
          <AsyncButton className="btn primary" onClick={() => act(`proposals/${openProposal.id}`, { action: "accept" }, t("proposal.status.accepted"))}>{t("proposal.accept")}</AsyncButton>
          <button className="btn" onClick={() => onOpen("propose")}><IconTag size={18} /> {t("deal.counter")}</button>
          <AsyncButton className="btn ghost" onClick={() => act(`proposals/${openProposal.id}`, { action: "decline" }, t("proposal.status.declined"))}>{t("proposal.decline")}</AsyncButton>
        </>
      );
    }
  } else if (d.status === "reserved") {
    title = t(seller ? "next.sellerReserved" : "next.buyerReserved");
    body = t(seller ? "next.sellerReservedBody" : "next.buyerReservedBody", { total: price });
    actions = seller ? (
      <button className="btn primary" onClick={() => onOpen("confirm")}><IconDeal size={18} /> {t("deal.confirmHandover")}</button>
    ) : (
      <>
        <button className="btn" onClick={() => onOpen("checklist")}><IconChecklist size={18} /> {t("deal.checklistN", { n: d.checklist.filter((x) => x.done).length, total: d.checklist.length })}</button>
        <button className="btn primary" onClick={() => onOpen("confirm")}><IconDeal size={18} /> {t("deal.confirmReceived")}</button>
      </>
    );
  } else if (d.status === "agreed") {
    title = t("next.agreed", { total: price });
    body = t(seller ? "next.agreedSellerBody" : "next.agreedBuyerBody");
    actions = (
      <>
        {!d.meetup && <button className="btn" onClick={() => onOpen("meetup")}><IconPin size={18} /> {t("deal.meetup")}</button>}
        {d.actions.reserve && (
          <AsyncButton className="btn primary" onClick={() => act("reserve", {}, t("deal.reservedToast"))}>{t("deal.reserve")}</AsyncButton>
        )}
      </>
    );
  } else {
    title = t(seller ? "next.openSeller" : "next.openBuyer");
    body = t(seller ? "next.openSellerBody" : "next.openBuyerBody");
    actions = seller ? (
      <>
        <button className="btn" onClick={() => onOpen("propose")}><IconTag size={18} /> {t("deal.propose")}</button>
        {d.actions.reserve && <AsyncButton className="btn primary" onClick={() => act("reserve", {}, t("deal.reservedToast"))}>{t("deal.reserveAt", { p: money(c.priceCents, c.currency) })}</AsyncButton>}
      </>
    ) : (
      c.negotiable && <button className="btn" onClick={() => onOpen("propose")}><IconTag size={18} /> {t("deal.propose")}</button>
    );
  }
  return (
    <section className={`next-step s-${d.status}`} aria-live="polite">
      <strong>{title}</strong>
      {body && <p dir="auto">{body}</p>}
      {meet}
      {actions && <div className="row wrap" style={{ gap: "var(--s2)" }}>{actions}</div>}
      {d.status !== "completed" && d.status !== "cancelled" && (
        <p className="tiny muted" style={{ margin: 0 }}>
          <IconShield size={14} /> {t("deal.directNote")}{" "}
          <button type="button" className="link" onClick={onSafety}>{t("safety.open")}</button>
        </p>
      )}
    </section>
  );
}

function MessageView({ m, currency, seller, otherName, canAnswer, onRespond }: { m: Msg; currency: string; seller: boolean; otherName: string; canAnswer: boolean; onRespond: (a: "accept" | "decline" | "withdraw") => Promise<void> }) {
  const { t, money, date } = useI18n();
  const toast = useToast();
  const time = <time dateTime={m.createdAt}>{date(m.createdAt, { hour: "numeric", minute: "2-digit", day: "numeric", month: "short" })}</time>;
  if (m.kind === "event") {
    const who = m.mine ? t("inbox.you") : otherName;
    const extra =
      m.body === "meetup" && m.meta.at ? ` · ${date(m.meta.at, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}` : "";
    const key = m.body === "contact_opened" ? (seller ? "event.contact_opened.seller" : "event.contact_opened.buyer") : `event.${m.body}`;
    return (
      <div className="event-line" role="note">
        <span dir="auto">
          {t(key as MessageKey, { who, p: m.meta.priceCents ? money(m.meta.priceCents * (m.meta.quantity ?? 1), currency) : "", place: m.meta.place ?? "", method: t(m.meta.method === "call" ? "contact.call" : "contact.whatsapp") })}
          {extra}
          {m.body === "cancelled" && m.meta.reason ? ` · ${m.meta.reason}` : ""}
        </span>
        {time}
      </div>
    );
  }
  if (m.kind === "proposal") {
    const total = money(m.meta.priceCents * (m.meta.quantity ?? 1), currency);
    const open = m.meta.status === "open";
    return (
      <div className={`proposal ${m.mine ? "mine" : ""} p-${m.meta.status}`}>
        <span className="tiny muted">{t(m.mine ? "proposal.yours" : "proposal.theirs", { name: otherName })}</span>
        <b className="price">{total}</b>
        {m.meta.quantity > 1 && <span className="small muted">{t("proposal.qty", { q: m.meta.quantity, p: money(m.meta.priceCents, currency) })}</span>}
        <span className={`tag ${m.meta.status === "accepted" ? "good" : m.meta.status === "declined" ? "bad" : ""}`}>{t(`proposal.status.${m.meta.status}` as MessageKey)}</span>
        {open && !m.mine && canAnswer && (
          <div className="row">
            <AsyncButton className="btn sm primary" onClick={() => onRespond("accept")}>{t("proposal.accept")}</AsyncButton>
            <AsyncButton className="btn sm" onClick={() => onRespond("decline")}>{t("proposal.decline")}</AsyncButton>
          </div>
        )}
        {open && m.mine && <AsyncButton className="btn sm ghost" onClick={() => onRespond("withdraw")}>{t("proposal.withdraw")}</AsyncButton>}
        {time}
      </div>
    );
  }
  if (m.kind === "contact") {
    const digits = String(m.meta.phone ?? "").replace(/\D/g, "");
    return (
      <div className={`bubble contact-card ${m.mine ? "mine" : ""}`}>
        <span className="small">{t(m.meta.method === "whatsapp" ? "msg.sharedWhatsapp" : "msg.sharedNumber")}</span>
        <b className="num" dir="ltr">{m.meta.phone}</b>
        {!m.mine && (
          <a className="btn sm" href={m.meta.method === "whatsapp" ? `https://wa.me/${digits}` : `tel:${m.meta.phone}`} target="_blank" rel="noreferrer">
            {m.meta.method === "whatsapp" ? <IconWhatsApp size={16} /> : <IconCall size={16} />} {t(m.meta.method === "whatsapp" ? "contact.whatsapp" : "contact.call")}
          </a>
        )}
        <span className="tiny">{t(m.meta.verified ? "contact.numberVerified" : "contact.numberUnverified")}</span>
        {time}
      </div>
    );
  }
  return (
    <div className={`bubble ${m.mine ? "mine" : ""}`} dir="auto">
      {m.body}
      {time}
      {!m.mine && (
        <button
          className="tiny bubble-report"
          onClick={async () => { await api("/api/reports", { body: { targetType: "message", targetId: m.id, reason: "abuse" } }); toast(t("common.reported")); }}
        >
          {t("inbox.reportMessage")}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sheets
// ---------------------------------------------------------------------------
function Sheet({ title, onClose, children, foot }: { title: string; onClose: () => void; children: ReactNode; foot?: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const { t } = useI18n();
  useEffect(() => {
    ref.current?.showModal();
  }, []);
  return (
    <dialog ref={ref} className="sheet" aria-labelledby={titleId} onClose={onClose}>
      <div className="sheet-head">
        <button className="icon-btn" onClick={() => ref.current?.close()} aria-label={t("common.close")}><IconX /></button>
        <h2 id={titleId}>{title}</h2>
      </div>
      <div className="sheet-body stack">{children}</div>
      {foot && <div className="sheet-foot">{foot}</div>}
    </dialog>
  );
}

function ProposeSheet({ thread, onClose, onSend }: { thread: ThreadResp; onClose: () => void; onSend: (priceCents: number, quantity: number) => Promise<void> }) {
  const { t, money } = useI18n();
  const c = thread.conversation;
  const [price, setPrice] = useState<number | null>(thread.deal.agreedPriceCents ?? c.priceCents);
  const [qty, setQty] = useState(thread.deal.quantity || 1);
  return (
    <Sheet
      title={t("deal.propose")}
      onClose={onClose}
      foot={
        <>
          <span className="small muted">{price ? t("proposal.total", { p: money(price * qty, c.currency) }) : ""}</span>
          <AsyncButton className="btn primary" disabled={!price} onClick={async () => { await onSend(price!, qty); onClose(); }}>{t("proposal.send")}</AsyncButton>
        </>
      }
    >
      <p className="muted" style={{ margin: 0 }}>{t("proposal.explain")}</p>
      <label className="label">{t("proposal.price")}</label>
      <MoneyInput value={price} onChange={setPrice} currency={c.currency} />
      <span className="tiny muted">{t("ask.asking", { p: money(c.priceCents, c.currency) })}</span>
      {c.quantityAvailable > 1 && (
        <div className="row between">
          <span className="label">{t("common.qty")}</span>
          <QtyStepper value={qty} max={c.quantityAvailable} onChange={setQty} label={t("common.qty")} />
        </div>
      )}
    </Sheet>
  );
}

function MeetupSheet({ deal, onClose, onSave }: { deal: Deal; onClose: () => void; onSave: (place: string, at: string | null) => Promise<void> }) {
  const { t } = useI18n();
  const [place, setPlace] = useState(deal.meetup?.place ?? "");
  const [at, setAt] = useState(deal.meetup?.at ? deal.meetup.at.slice(0, 16) : "");
  const ideas = ["meet.mall", "meet.cafe", "meet.shop", "meet.station"] as const;
  return (
    <Sheet
      title={t("deal.meetup")}
      onClose={onClose}
      foot={
        <>
          <span />
          <AsyncButton className="btn primary" disabled={place.trim().length < 2} onClick={async () => { await onSave(place.trim(), at ? new Date(at).toISOString() : null); onClose(); }}>{t("meet.send")}</AsyncButton>
        </>
      }
    >
      <Notice>{t("meet.public")}</Notice>
      <div className="chips" style={{ flexWrap: "wrap" }}>
        {ideas.map((k) => <button key={k} type="button" className="chip" onClick={() => setPlace(t(k))}>{t(k)}</button>)}
      </div>
      <label className="label" htmlFor="meet-place">{t("meet.place")}</label>
      <input id="meet-place" className="input" value={place} onChange={(e) => setPlace(e.target.value)} maxLength={200} dir="auto" />
      <label className="label" htmlFor="meet-at">{t("meet.when")} <span className="muted small">({t("common.optional")})</span></label>
      <input id="meet-at" className="input" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} dir="ltr" />
    </Sheet>
  );
}

function ChecklistSheet({ deal, onClose, onToggle }: { deal: Deal; onClose: () => void; onToggle: (item: string, done: boolean) => Promise<void> }) {
  const { t } = useI18n();
  // Ticks show at once (at a handover there's no time to wait on a slow connection) and undo if saving fails.
  const [items, setItems] = useState(deal.checklist);
  const done = items.filter((c) => c.done).length;
  const set = (key: string, value: boolean) => setItems((xs) => xs.map((x) => (x.key === key ? { ...x, done: value } : x)));
  return (
    <Sheet title={t("deal.checklist")} onClose={onClose} foot={<><span className="small muted">{t("deal.checklistN", { n: done, total: items.length })}</span><button className="btn primary" onClick={onClose}>{t("common.done")}</button></>}>
      <p className="muted" style={{ margin: 0 }}>{t("check.intro")}</p>
      <ul className="checklist">
        {items.map((c) => (
          <li key={c.key}>
            <label>
              <input
                type="checkbox"
                checked={c.done}
                onChange={async (e) => {
                  const value = e.target.checked;
                  set(c.key, value);
                  try {
                    await onToggle(c.key, value);
                  } catch {
                    set(c.key, !value);
                  }
                }}
              />
              <span>
                <b>{t(`check.${c.key}` as MessageKey)}</b>
                <span className="small muted">{t(`check.${c.key}.how` as MessageKey)}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}

function ShareSheet({ onClose, onShare }: { onClose: () => void; onShare: (method: "whatsapp" | "call") => Promise<void> }) {
  const { t } = useI18n();
  return (
    <Sheet title={t("deal.shareNumber")} onClose={onClose}>
      <p className="muted" style={{ margin: 0 }}>{t("share.explain")}</p>
      <AsyncButton className="btn wa block" onClick={async () => { await onShare("whatsapp"); onClose(); }}><IconWhatsApp size={18} /> {t("share.whatsapp")}</AsyncButton>
      <AsyncButton className="btn block" onClick={async () => { await onShare("call"); onClose(); }}><IconCall size={18} /> {t("share.call")}</AsyncButton>
      <Link className="link small" to="/account?tab=contact">{t("share.manage")}</Link>
    </Sheet>
  );
}

function ReviewForm({ dealId, otherName, onDone }: { dealId: string; otherName: string; onDone: () => Promise<void> }) {
  const { t } = useI18n();
  const toast = useToast();
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState("");
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!open) return <button className="btn" onClick={() => setOpen(true)}><IconStar size={18} /> {t("review.rate", { name: otherName })}</button>;
  return (
    <div className="card stack review-form" style={{ width: "100%" }}>
      <div className="stars" role="radiogroup" aria-label={t("review.rate", { name: otherName })}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={t("seller.stars", { n })} className={n <= rating ? "on" : ""} onClick={() => setRating(n)}>★</button>
        ))}
      </div>
      <textarea className="input" value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("review.body")} maxLength={1000} dir="auto" />
      {err && <Notice kind="bad">{err}</Notice>}
      <AsyncButton
        className="btn primary"
        disabled={!rating}
        onClick={async () => {
          setErr(null);
          try {
            await api(`/api/conversations/${dealId}/review`, { body: { rating, body } });
            toast(t("review.thanks"));
            await onDone();
          } catch (e) {
            setErr((e as ApiError).message);
          }
        }}
      >
        {t("review.post")}
      </AsyncButton>
    </div>
  );
}
